// src/store/slices/viewportSlice.ts
/**
 * Viewport state management - handles pan, zoom, orientation
 * Implements Section 5.1 of the specification
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ViewportState } from '../../types/redux';

const initialState: ViewportState = {
  scale: 83, // 83 pixels per unit (default zoom level)
  offsetX: 400, // FIXED: Center origin in default 800px viewport (half of 800)
  offsetY: 200, // Center origin in default 400px viewport height (half of 400)
  orientation: 'horizontal',
  showNegatives: true,
  isDragging: false,
  lastDragPosition: null,
};

export const viewportSlice = createSlice({
  name: 'viewport',
  initialState,
  reducers: {
    /**
     * Set the scale factor (pixels per unit)
     * @param scale - New scale value, clamped to reasonable bounds
     */
    setScale: (state, action: PayloadAction<number>) => {
      // Clamp scale between 10px/unit and 1000px/unit
      state.scale = Math.max(10, Math.min(1000, action.payload));
    },

    /**
     * Set horizontal offset
     * @param offsetX - New offset in pixels
     */
    setOffsetX: (state, action: PayloadAction<number>) => {
      state.offsetX = action.payload;
    },

    /**
     * Set vertical offset
     * @param offsetY - New offset in pixels
     */
    setOffsetY: (state, action: PayloadAction<number>) => {
      state.offsetY = action.payload;
    },

    /**
     * Pan by a relative amount (supports both horizontal and vertical)
     * @param delta - Change in offset (pixels) - can be number for X only or object for X/Y
     */
    pan: (state, action: PayloadAction<number | { deltaX?: number; deltaY?: number }>) => {
      if (typeof action.payload === 'number') {
        // Legacy support: number means pan X axis
        state.offsetX += action.payload;
      } else {
        // New format: object with deltaX and/or deltaY
        if (action.payload.deltaX !== undefined) {
          state.offsetX += action.payload.deltaX;
        }
        if (action.payload.deltaY !== undefined) {
          state.offsetY += action.payload.deltaY;
        }
      }
    },

    /**
     * Zoom at a specific point, keeping that point stable
     * @param payload - { scale, anchorX, anchorY? } where anchor is in pixels
     * For horizontal mode, uses anchorX; for vertical mode, uses anchorY
     */
    zoomAt: (state, action: PayloadAction<{ scale: number; anchorX: number; anchorY?: number }>) => {
      const { scale, anchorX, anchorY } = action.payload;
      const oldScale = state.scale;
      const newScale = Math.max(10, Math.min(1000, scale));
      
      if (state.orientation === 'horizontal') {
        // Calculate the world value at the anchor point
        const worldValue = (anchorX - state.offsetX) / oldScale;
        
        // Update scale and adjust offset to keep world value at same pixel position
        state.scale = newScale;
        state.offsetX = anchorX - worldValue * newScale;
      } else {
        // Vertical mode: use anchorY for the world value calculation
        const anchor = anchorY ?? anchorX; // Fall back to anchorX if anchorY not provided
        const worldValue = (anchor - state.offsetY) / oldScale;
        
        // Update scale and adjust offset to keep world value at same pixel position
        state.scale = newScale;
        state.offsetY = anchor - worldValue * newScale;
      }
    },

    /**
     * Toggle orientation between horizontal and vertical
     */
    toggleOrientation: (state) => {
      state.orientation = state.orientation === 'horizontal' ?
        'vertical' : 'horizontal';
    },

    /**
     * Set orientation explicitly
     */
    setOrientation: (state, action: PayloadAction<'horizontal' | 'vertical'>) => {
      state.orientation = action.payload;
    },

    /**
     * Toggle negative number display
     */
    toggleNegatives: (state) => {
      state.showNegatives = !state.showNegatives;
    },

    /**
     * Set negative number display explicitly
     */
    setShowNegatives: (state, action: PayloadAction<boolean>) => {
      state.showNegatives = action.payload;
    },

    /**
     * Center the origin (value 0) in the viewport
     * @param viewportSize - Width (horizontal) or height (vertical) of the viewport in pixels
     */
    centerOrigin: (state, action: PayloadAction<number | { width: number; height: number }>) => {
      if (typeof action.payload === 'number') {
        // Legacy support: single number means viewport width for horizontal mode
        state.offsetX = action.payload / 2;
      } else {
        // New format: object with width and height
        state.offsetX = action.payload.width / 2;
        state.offsetY = action.payload.height / 2;
      }
    },

    /**
     * Jump to a specific value, centering it in the viewport
     * @param payload - { value, viewportWidth, viewportHeight? }
     */
    jumpToValue: (state, action: PayloadAction<{ value: number; viewportWidth: number; viewportHeight?: number }>) => {
      const { value, viewportWidth, viewportHeight } = action.payload;
      if (state.orientation === 'horizontal') {
        // Center the value in the viewport horizontally
        state.offsetX = viewportWidth / 2 - value * state.scale;
      } else {
        // Center the value in the viewport vertically
        const height = viewportHeight ?? viewportWidth;
        state.offsetY = height / 2 - value * state.scale;
      }
    },

    /**
     * Reset viewport to default state
     */
    resetViewport: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Load viewport state from shared scene
     */
    loadViewportState: (state, action: PayloadAction<ViewportState>) => {
      Object.assign(state, action.payload);
    },

    /**
     * ADDED: Auto-center viewport for a given width without changing other properties
     * This is used for responsive centering when the container width changes
     */
    autoCenterViewport: (state, action: PayloadAction<number>) => {
      const viewportWidth = action.payload;
      // Only auto-center if we're at the default offset or very close to it
      // This prevents overriding user-initiated panning
      const isNearDefault = Math.abs(state.offsetX - 400) < 50;
      if (isNearDefault) {
        state.offsetX = viewportWidth / 2;
      }
    },

    /**
     * Set viewport to show a specific domain range
     * @param payload - { start, end } domain values
     */
    setDomain: (state, action: PayloadAction<{ start: number; end: number }>) => {
      const { start, end } = action.payload;
      const domainWidth = end - start;
      
      // Calculate scale to fit domain in 800px viewport with some padding
      const targetWidth = 700; // Leave 50px padding on each side
      state.scale = targetWidth / domainWidth;
      
      // Clamp scale to reasonable bounds
      state.scale = Math.max(10, Math.min(1000, state.scale));
      
      // Center the domain in the viewport
      const domainCenter = (start + end) / 2;
      const viewportCenter = 400; // Default viewport center
      state.offsetX = viewportCenter - domainCenter * state.scale;
    },
  },
});

export const {
  setScale,
  setOffsetX,
  setOffsetY,
  pan,
  zoomAt,
  toggleOrientation,
  setOrientation,
  toggleNegatives,
  setShowNegatives,
  centerOrigin,
  jumpToValue,
  resetViewport,
  autoCenterViewport,
  setDomain,
  loadViewportState,
} = viewportSlice.actions;

export default viewportSlice.reducer;

// Selectors for computed values
export const selectViewportScale = (state: { viewport: ViewportState }) => state.viewport.scale;
export const selectViewportOffset = (state: { viewport: ViewportState }) => state.viewport.offsetX;
export const selectViewportOffsetY = (state: { viewport: ViewportState }) => state.viewport.offsetY;
export const selectIsHorizontal = (state: { viewport: ViewportState }) => 
  state.viewport.orientation === 'horizontal';
export const selectShowNegatives = (state: { viewport: ViewportState }) => 
  state.viewport.showNegatives;